import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { getPendingCheckout, clearPendingCheckout } from "@/lib/checkout";
import { api, getToken, setToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";
import { FindoSpinner } from "@/components/FindoSpinner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Mail, Phone, Truck, FileDown } from "lucide-react";

export default function PaymentResult() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh, setPartner } = useAuth();
  const { t } = useLang();
  const isCancel = pathname.includes("cancel");
  const isSignupCancel = isCancel && params.get("signup") === "1";
  const sessionId = params.get("session_id");
  const [state, setState] = useState(isCancel ? "cancel" : "loading");
  const [meta, setMeta] = useState(null);
  const [contacts, setContacts] = useState(null);

  // Boost/subscription success → tailored message + auto-redirect to the space.
  const BOOST_REDIRECT = { ad_boost: "/mon-compte", partner_boost: "/b2b/establishments" };
  const isPublication = meta?.kind === "ad_publication";
  const isContribution = meta?.kind === "finder_contribution";
  const boostTarget = BOOST_REDIRECT[meta?.kind];
  const isSubscription = meta?.kind === "subscription";
  const isSignup = meta?.kind === "subscription_signup";
  const subLike = isSubscription || isSignup;
  const isInvoice = meta?.kind === "invoice" || meta?.kind === "monthly_invoice";
  const redirectTarget = isPublication ? "/mon-compte" : isContribution ? `/suivi-trouve/${meta.ad_id}` : boostTarget || (subLike ? "/b2b/establishments" : isInvoice ? "/b2b/account" : null);
  // Cancelled payment: send the user back to the step they paid from (resume banner shows there).
  useEffect(() => {
    if (!isCancel) return;
    const pending = getPendingCheckout();
    if (pending?.returnTo) navigate(pending.returnTo, { replace: true });
  }, [isCancel, navigate]);

  useEffect(() => {
    if (state === "success") clearPendingCheckout();
  }, [state]);

  useEffect(() => {
    if (state !== "success" || !redirectTarget) return;
    const id = setTimeout(() => navigate(redirectTarget), 2800);
    return () => clearTimeout(id);
  }, [state, redirectTarget, navigate]);

  useEffect(() => {
    if (isCancel || !sessionId) { if (!sessionId && !isCancel) setState("done"); return; }
    let tries = 0;
    async function poll() {
      try {
        const s = await api(`/checkout/status/${sessionId}`, { auth: false });
        setMeta(s.metadata || {});
        if (s.payment_status === "paid" || s.status === "complete") {
          const kind = s.metadata?.kind;
          if (kind === "subscription_signup") {
            // Paid-plan signup: the account is created server-side on payment.
            // Finalize + auto-login so the new partner lands in their space.
            for (let i = 0; i < 4; i++) {
              try {
                const res = await api("/auth/finalize-signup", { method: "POST", auth: false, body: { session_id: sessionId } });
                setToken(res.access_token);
                setPartner(res.partner);
                try { localStorage.removeItem("findo_plan_cycle"); localStorage.removeItem("findo_plan_region"); localStorage.removeItem("findo_plan_tier"); } catch {}
                break;
              } catch { await new Promise((r) => setTimeout(r, 1300)); }
            }
          }
          if (kind === "subscription" && getToken()) {
            // Retry applying (Stripe may lag marking the session paid), then ALWAYS
            // refresh the profile so the dashboard reflects the new plan even if the
            // auth-apply call errors (the public poll above already applies it server-side).
            for (let i = 0; i < 3; i++) {
              try { await api(`/checkout/apply-subscription?session_id=${sessionId}`, { method: "POST" }); break; }
              catch { await new Promise((r) => setTimeout(r, 1200)); }
            }
            try { await refresh(); } catch {}
          }
          if (kind === "recovery" && s.metadata?.declaration_id) {
            try { setContacts(await api(`/declarations/${s.metadata.declaration_id}/paid-contacts?session_id=${sessionId}`, { auth: false })); } catch {}
          }
          setState("success");
          return;
        }
      } catch {}
      if (tries++ < 6) setTimeout(poll, 1500);
      else setState("pending");
    }
    poll();
  }, [sessionId, isCancel, refresh]);

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-border p-8 card-shadow text-center animate-fade-up">
        <Link to="/" className="flex justify-center mb-6"><Logo size={40} withText textClass="text-2xl text-slate-900" /></Link>

        {state === "loading" && (<><div className="flex justify-center"><FindoSpinner size={44} /></div><p className="mt-4 text-slate-600">{t("pub.verifyingPayment")}</p></>)}

        {state === "success" && (
          <div data-testid="payment-success">
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-teal-100 text-teal-700"><CheckCircle2 size={34} /></div>
            <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">
              {isPublication ? t("fa.pubSuccessTitle") : isContribution ? t("fa.contribSuccessTitle") : subLike ? t("hc.paymentSuccessful") : boostTarget ? t("pr2.boostActivated") : t("pr2.paymentSuccess")}
            </h1>
            <p className="mt-2 text-slate-600" data-testid="payment-success-message">
              {isPublication ? t("fa.pubSuccessMsg") : isContribution ? t("fa.contribSuccessMsg") : meta?.kind === "ad_boost"
                ? t("pr2.adBoostMsg")
                : meta?.kind === "partner_boost"
                  ? t("pr2.partnerBoostMsg")
                  : subLike
                    ? t("hc.yourAccountIsActiveYou")
                    : isInvoice
                      ? t("hc.yourPaymentHasBeenRecorded")
                      : meta?.kind === "shipping"
                        ? t("hc.yourShipmentIsBeingPrepared")
                        : t("pr2.canRecoverNow")}
            </p>
            {meta?.kind === "recovery" && (
              <div className="mt-4 rounded-xl bg-findo-indigo/5 border border-findo-indigo/20 p-3 text-sm text-slate-600" data-testid="pr-fee-recipient-note">
                {t("hc.recoveryFeesArePaidDirectly")}
              </div>
            )}
            {redirectTarget && (
              <p className="mt-3 flex items-center justify-center gap-2 text-sm text-slate-400" data-testid="payment-redirect-note">
                <FindoSpinner size={18} /> {t("pr2.redirectingSpace")}
              </p>
            )}
            {contacts && (
              <div className="mt-5 rounded-xl bg-slate-50 border border-border p-4 text-left text-sm">
                <p className="font-bold text-slate-700 mb-2">{t("pub.partnerContact")}</p>
                <p className="font-semibold text-slate-800">{contacts.partner?.name}</p>
                {contacts.partner?.email && <p className="flex items-center gap-1.5 text-slate-600 mt-1"><Mail size={13} /> {contacts.partner.email}</p>}
                {contacts.partner?.phone && <p className="flex items-center gap-1.5 text-slate-600 mt-1"><Phone size={13} /> {contacts.partner.phone}</p>}
              </div>
            )}
            <Link to={redirectTarget || (subLike ? "/b2b/establishments" : "/mon-compte")}>
              <Button className="mt-6 rounded-xl font-semibold w-full" data-testid="payment-continue-btn">
                {boostTarget ? t("pr2.goToSpaceNow") : t("pr2.continue")}
              </Button>
            </Link>
            {meta?.kind === "recovery" && meta?.declaration_id && (
              <a href={`${API}/declarations/${meta.declaration_id}/receipt.pdf`} target="_blank" rel="noopener noreferrer"
                className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-border py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="pr-receipt-btn">
                <FileDown size={16} /> {t("pr2.downloadReceiptPdf")}
              </a>
            )}
            {meta?.kind === "recovery" && meta?.declaration_id && (
              <Link to={`/ship/${meta.declaration_id}`}>
                <Button variant="outline" className="mt-3 rounded-xl font-semibold w-full" data-testid="pr-ship-btn">
                  <Truck size={16} className="mr-1.5" /> {t("pr.shipBtn")}
                </Button>
              </Link>
            )}
          </div>
        )}

        {state === "pending" && (<><div className="flex justify-center"><FindoSpinner size={44} /></div><p className="mt-4 text-slate-600">{t("pub.paymentProcessing")}</p></>)}

        {state === "cancel" && (
          <div data-testid="payment-cancel">
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-red-100 text-red-600"><XCircle size={34} /></div>
            <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">{t("pub.paymentCancelled")}</h1>
            {isSignupCancel ? (
              <>
                <p className="mt-2 text-slate-600" data-testid="signup-cancel-message">
                  {t("hc.paymentNotCompletedYourAccount")}
                </p>
                <Link to="/auth/register">
                  <Button className="mt-6 rounded-xl font-semibold w-full" data-testid="signup-cancel-retry">
                    {t("hc.resumeRegistration")}
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <p className="mt-2 text-slate-600">{t("pub.noAmountCharged")}</p>
                <Link to="/"><Button variant="outline" className="mt-6 rounded-xl font-semibold w-full">{t("pub.backHome")}</Button></Link>
              </>
            )}
          </div>
        )}

        {state === "done" && (<><p className="text-slate-600">{t("pub.noPaymentSession")}</p><Link to="/"><Button className="mt-4 rounded-xl">{t("pub.home")}</Button></Link></>)}
      </div>
    </div>
  );
}
